import { normalizeApiError } from '../api/apiError';
import { createReadAsyncThunk } from "../store/createReadAsyncThunk";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { PcsEnrolment } from '../api/endpoints/pcsEnrolmentApi';
import { ApiRequestStatus } from "../enums/apiRequestStatus";
import { PcsEnrolmentYear } from "../../features/pcsPlan/models/pcsEnrolmentYear";

interface PcsEnrolmentState {
    enrolmentYears: PcsEnrolmentYear[] | null;
    basketId: string | null;
    canPayMembership: boolean | null;
    finalisePaymentCheck: boolean | null;
    enrolmentYearsStatus: ApiRequestStatus;
    basketIdStatus: ApiRequestStatus;
    canPayMembershipStatus: ApiRequestStatus;
    finalisePaymentCheckStatus: ApiRequestStatus;
}

const initialState: PcsEnrolmentState = {
    enrolmentYears: null,
    basketId: null,
    canPayMembership: null,
    finalisePaymentCheck: null,
    enrolmentYearsStatus: ApiRequestStatus.Idle,
    basketIdStatus: ApiRequestStatus.Idle,
    canPayMembershipStatus: ApiRequestStatus.Idle,
    finalisePaymentCheckStatus: ApiRequestStatus.Idle
}

export const getPcsEnrolmentYearsAsync = createReadAsyncThunk<PcsEnrolmentYear[]>(
    'pcsEnrolment/getPcsEnrolmentYearsAsync',
    async (_, thunkAPI) => {
        try {
            return await PcsEnrolment.getPcsEnrolmentYearsAsync();
        } catch (error: unknown) {
            return thunkAPI.rejectWithValue(normalizeApiError(error));
        }
    }
)

export const enrolBasketAsync = createAsyncThunk<string, boolean>(
    'pcsEnrolment/enrolBasketAsync',
    async (isCombinedPayment, thunkAPI) => {
        try {
            return await PcsEnrolment.enrolBasketAsync(isCombinedPayment);
        } catch (error: unknown) {
            return thunkAPI.rejectWithValue(normalizeApiError(error));
        }
    }
)

export const canPayMembershipAsync = createReadAsyncThunk<boolean>(
    'pcsEnrolment/canPayMembershipAsync',
    async (_, thunkAPI) => {
        try {
            return await PcsEnrolment.canPayMembershipAsync();
        } catch (error: unknown) {
            return thunkAPI.rejectWithValue(normalizeApiError(error));
        }
    }
)

export const finalisePaymentCheckAsync = createAsyncThunk<boolean>(
    'pcsEnrolment/finalisePaymentCheckAsync',
    async (_, thunkAPI) => {
        try {
            return await PcsEnrolment.finalisePaymentCheckAsync();
        } catch (error: unknown) {
            return thunkAPI.rejectWithValue(normalizeApiError(error));
        }
    }
)

export const pcsEnrolmentSlice = createSlice({
    name: 'pcsEnrolmentSlice',
    initialState,
    reducers: {

    },
    extraReducers: (builder => {
        builder.addCase(enrolBasketAsync.pending, (state) => {
            state.basketIdStatus = ApiRequestStatus.Pending;
        });
        builder.addCase(enrolBasketAsync.fulfilled, (state, action) => {
            state.basketId = action.payload;
            state.basketIdStatus = ApiRequestStatus.Fulfilled;
        });
        builder.addCase(enrolBasketAsync.rejected, (state) => {
            state.basketIdStatus = ApiRequestStatus.Rejected;
        });

        builder.addCase(getPcsEnrolmentYearsAsync.pending, (state) => {
            state.enrolmentYearsStatus = ApiRequestStatus.Pending;
        });

        builder.addCase(getPcsEnrolmentYearsAsync.fulfilled, (state, action) => {
            state.enrolmentYears = action.payload;
            state.enrolmentYearsStatus = ApiRequestStatus.Fulfilled;
        });

        builder.addCase(getPcsEnrolmentYearsAsync.rejected, (state) => {
            state.enrolmentYearsStatus = ApiRequestStatus.Rejected;
        });

        builder.addCase(canPayMembershipAsync.pending, (state) => {
            state.canPayMembershipStatus = ApiRequestStatus.Pending;
        });
        builder.addCase(canPayMembershipAsync.fulfilled, (state, action) => {
            state.canPayMembership = action.payload;
            state.canPayMembershipStatus = ApiRequestStatus.Fulfilled;
        });
        builder.addCase(canPayMembershipAsync.rejected, (state) => {
            state.canPayMembershipStatus = ApiRequestStatus.Rejected;
        });

        builder.addCase(finalisePaymentCheckAsync.pending, (state) => {
            state.finalisePaymentCheckStatus = ApiRequestStatus.Pending;
        });
        builder.addCase(finalisePaymentCheckAsync.fulfilled, (state, action) => {
            state.finalisePaymentCheck = action.payload;
            state.finalisePaymentCheckStatus = ApiRequestStatus.Fulfilled;
        });
        builder.addCase(finalisePaymentCheckAsync.rejected, (state) => {
            state.finalisePaymentCheckStatus = ApiRequestStatus.Rejected;
        });
    })
})
